import { Button, Dialog, DialogActions, DialogContent } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { BarcodeScannerErrorModal } from './BarcodeScannerErrorModal';
import { AddOrUpdateBarcodeMappingRequest, client } from '../../../../app/services/api/orderManagementClient';
import { UserContext } from '../../../../components/shared/useUser';
import { StockCheckItem } from '../../../../app/services/api/generated';
import { BarcodeStockItemDetails } from '../BarcodeStockItemDetails';
import useLogError from '../../../../hooks/useLogError';
import negativeSound from '../../../../assets/sounds/barcodeNegative.mp3';
import positiveSound from '../../../../assets/sounds/barcodePositive.mp3';
import useSound from 'use-sound';
import { useTranslation } from 'react-i18next';
import { WasmBarcodeScanner2 } from './WasmScanner2';
import BarcodeMappingAddEditModal from '../../../ManageBarcodes/components/modals/BarcodeMappingAddEditModal';
import useNswagClient from '../../../../hooks/api/useNswagClient';

interface BarcodeScannerModalProps {
  isScannerOpen: boolean;
  closeModal: () => void;
  stockCheckItems?: StockCheckItem[];
  setStockCheckItems?: React.Dispatch<React.SetStateAction<StockCheckItem[]>>;
  stockCountName?: string;
  stockCountId?: number;
  hasBarcodeScanningPermission?: boolean;
  onDetected?: (code: string) => void;
  onError?: (err: Error) => void;
}

export const BarcodeScannerModal = (props: BarcodeScannerModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isApiErrorModalOpen, setIsApiErrorModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [decodedText, setDecodedText] = useState('');
  const [stockCheck, setStockCheck] = useState<StockCheckItem | undefined>();
  const [isItemDetailsModalOpen, setIsItemDetailsModalOpen] = useState(false);
  const [uom, setUom] = useState('');
  const [isMappingOpen, setIsMappingOpen] = useState<boolean>(false);
  const [mappingBarcode, setMappingBarcode] = useState<string>('');
  const [hasWritePermission, setHasWritePermission] = useState<boolean>(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);


  const { logError } = useLogError();
  const { emitScanEvent } = useNswagClient();

  const { t } = useTranslation('stockCountScanning');
  const { t: barcode } = useTranslation('barcodeMapping');

  const [playPositive] = useSound(positiveSound);
  const [playNegative] = useSound(negativeSound);

  const { selectedStore, user, hasPermissionTo } = useContext(UserContext);

  useEffect(() => {
    setHasWritePermission(hasPermissionTo(['BarcodeManagementWrite']));
  }, [hasPermissionTo]);

  const handleBarcodeScannLogging = (stockCountId: number, itemId?: number, barcode?: string) => {
    if (props.hasBarcodeScanningPermission) {
      emitScanEvent(stockCountId, itemId, barcode)
        .then(() => {
          if (itemId && props.setStockCheckItems) {
            props.setStockCheckItems(prevItems => prevItems.map(item =>
              item.id === itemId
                ? { ...item, scanStatus: 1 }
                : item,
            ));
          }
        })
        .catch(err => {
          logError(err);
        });
    }
  };

  const handleCloseModal = () => {
    props.closeModal();
  };

  const handleScannerLog = (message: string) => {
    console.log(message);
  };

  const handlerScannerCapture = (code: string) => {
    if (isLoading) return;   // prevent duplicate processing

    if (code) {
      setDecodedText(code);

      if (props.onDetected) {
        try {
          props.onDetected(code);
        } catch (err) {
          props.onError?.(err as Error);
        }
        props.closeModal();
      } else {
        handleItemScanned(code);
      }
    }
  };

  const handleItemScanned = async (barcode: string) => {
    if (isLoading) return;

    setIsLoading(true);
    client.getItemByBarcode(selectedStore?.storeNumber, barcode, props.stockCountName, user?.username)
      .then((result) => {
        if (result.data == null) {
          playNegative();
          if (props.stockCountId) {
            handleBarcodeScannLogging(props.stockCountId, undefined, barcode);
          }
          if (hasWritePermission) {
            // User has permission to create barcode mapping
            setMappingBarcode(barcode);
            setIsMappingOpen(true);
          } else {
            // User doesn't have permission, just show error
            setIsErrorModalOpen(true);
          }
        }
        else {
          const itemNumber = result.data.sku;
          const stockCheck = props.stockCheckItems?.find(x => x.itemNumber === itemNumber);
          const uom = result.data.barcodeUom;

          if (stockCheck) {
            playPositive();
            if (props.stockCountId) {
              handleBarcodeScannLogging(props.stockCountId, stockCheck.id, barcode);
            }
            setStockCheck(stockCheck);
            setUom(uom);
            setIsItemDetailsModalOpen(true);
          }
          else {
            playNegative();
            if (props.stockCountId) {
              handleBarcodeScannLogging(props.stockCountId, undefined, barcode);
            }
            setIsWarningModalOpen(true);
          }
        }

      })
      .catch((error => {
        logError(error);
        setIsApiErrorModalOpen(true);
      }))
      .finally(() => {
        setIsLoading(false);
      },
      );
  };

  const handleSaveMapping = async (req: AddOrUpdateBarcodeMappingRequest) => {
    try {
      const result = await client.addOrUpdateBarcodeMapping(selectedStore?.storeNumber, req);
      if (!result.success) throw new Error(result.message);

      // Close the mapping modal and show success
      setIsMappingOpen(false);
      setSuccessModalOpen(true);
    }
    catch (err: unknown) {
      logError(err);
      // Show error in the error modal
      setIsApiErrorModalOpen(true);
    }
  };

  const handleSuccessModalClose = () => {
    setSuccessModalOpen(false);
    // Re-scan the barcode after successful mapping
    handleItemScanned(mappingBarcode);
  };

  const handleItemDetailsClose = () => {
    setIsItemDetailsModalOpen(false);
    // Reset the decoded text so we can scan another item
    setDecodedText('');
  };

  return (
    <Dialog open={props.isScannerOpen}
      fullScreen
      sx={{ height: '100dvh' }}>
      <DialogContent sx={{ p: '0px', pb: '4px', m: '0px', flexGrow: 1, flexShrink: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        <WasmBarcodeScanner2 onLog={handleScannerLog}
          onCapture={handlerScannerCapture}
          lastScannedCode={decodedText} />
      </DialogContent>
      <DialogActions sx={{ flexShrink: 0, justifyContent: 'flex-end', ml: '10px', mr: '10px', mt: '12px', mb: '10px', pb: 'calc(10px + env(safe-area-inset-bottom, 0px))' }}>
        <Button variant='secondary'
          size='md'
          disabled={isLoading}
          onClick={handleCloseModal}>{t('close')}</Button>
      </DialogActions>
      <div id='qr-reader'></div>
      {props.setStockCheckItems && (
        <BarcodeStockItemDetails stockCheck={stockCheck}
          isOpen={isItemDetailsModalOpen}
          closeModal={handleItemDetailsClose}
          uom={uom}
          setStockCheckItems={props.setStockCheckItems} />
      )}
      <BarcodeScannerErrorModal isOpen={isErrorModalOpen}
        closeModal={() => setIsErrorModalOpen(false)}
        title={t('errorTitle')}
        content={t('errorContent') + decodedText} />
      <BarcodeScannerErrorModal isOpen={isApiErrorModalOpen}
        closeModal={() => setIsApiErrorModalOpen(false)}
        title={t('apiErrorTitle')}
        content={t('apiErrorContent') + decodedText} />
      <BarcodeScannerErrorModal isOpen={isWarningModalOpen}
        closeModal={() => setIsWarningModalOpen(false)}
        title={t('warningTitle')}
        content={t('warningContent')} />
      <BarcodeScannerErrorModal isOpen={successModalOpen}
        closeModal={handleSuccessModalClose}
        title='Success'
        content={barcode('successSavingMapping')} />
      <BarcodeMappingAddEditModal
        storeNumber={selectedStore?.storeNumber ?? ''}
        open={isMappingOpen}
        mapping={null}
        onClose={() => setIsMappingOpen(false)}
        onSave={handleSaveMapping}
        initialBarcode={mappingBarcode}
        allowScanning={false}
        helperMessage={t('missingBarcode')}
      />
    </Dialog>
  );
};